/**
 * Answers Hub — AI-targeted comparison & educational content
 * Each page targets high-volume "People Also Ask" and Featured Snippet queries.
 * Structure: question-led H1, direct answer paragraph, expanded detail, comparison table, FAQs.
 */

export interface AnswerPage {
  slug: string;
  title: string;
  metaDescription: string;
  h1: string;
  directAnswer: string;
  sections: AnswerSection[];
  faqs: { question: string; answer: string }[];
  relatedPages: string[];
}

export interface AnswerSection {
  heading: string;
  content: string;
  list?: string[];
  table?: { headers: string[]; rows: string[][] };
}

export const answersPages: AnswerPage[] = [
  {
    slug: "how-much-do-fitted-wardrobes-cost",
    title: "How Much Do Fitted Wardrobes Cost in 2026? | Price Guide",
    metaDescription: "Fitted wardrobes cost £1,499–£5,999 in the UK in 2026. Get a full price breakdown by type, size, material, and region. Includes comparison with Sharps, Hammonds & IKEA PAX.",
    h1: "How Much Do Fitted Wardrobes Cost in 2026?",
    directAnswer: "In 2026, bespoke fitted wardrobes in the UK cost between £1,499 and £5,999 depending on size, materials, and internal configuration. A single wardrobe starts from £1,499, a double from £2,499, and a full wall of floor-to-ceiling wardrobes from £3,499 to £5,999. All Smiths prices include VAT, professional installation, and a 10-year guarantee.",
    sections: [
      {
        heading: "Price Breakdown by Wardrobe Type",
        content: "The cost of fitted wardrobes varies significantly based on the type, size, and complexity of the installation.",
        table: {
          headers: ["Wardrobe Type", "Price Range", "Typical Size", "Lead Time"],
          rows: [
            ["Single Hinged Wardrobe", "£1,499 – £2,499", "600mm – 1000mm wide", "3 weeks"],
            ["Double Hinged Wardrobe", "£2,499 – £3,999", "1200mm – 1800mm wide", "3 weeks"],
            ["Full Wall Floor-to-Ceiling", "£3,499 – £5,999", "2400mm – 4000mm wide", "3 weeks"],
            ["Sliding Door Wardrobe", "£1,999 – £4,499", "1500mm – 3000mm wide", "3 weeks"],
            ["Walk-In Wardrobe", "£4,999 – £12,000", "Full room", "4 weeks"],
            ["Loft Wardrobe (sloped ceiling)", "£2,499 – £5,999", "Custom to roofline", "3 weeks"],
          ],
        },
      },
      {
        heading: "What Affects the Cost?",
        content: "Five main factors determine your final price:",
        list: [
          "Size and number of doors — larger wardrobes with more doors cost more",
          "Door finish — matt, gloss, woodgrain, painted, or handleless options at different price points",
          "Internal configuration — more drawers, pull-out accessories, and specialist storage increases cost",
          "Room complexity — sloped ceilings, alcoves, and chimney breasts require custom scribing",
          "Lighting and accessories — LED strips, soft-close hinges, pull-down rails add £200–£800",
        ],
      },
      {
        heading: "Smiths vs Competitors: Price Comparison",
        content: "How Smiths compares to other major fitted wardrobe companies in the UK:",
        table: {
          headers: ["Company", "Single Wardrobe", "Full Wall", "Lead Time", "Guarantee"],
          rows: [
            ["Smiths", "From £1,499", "From £3,499", "3 weeks", "10 years"],
            ["Sharps", "From £2,500", "From £5,000", "6–8 weeks", "10 years"],
            ["Hammonds", "From £2,200", "From £4,500", "8–12 weeks", "10 years"],
            ["Neville Johnson", "From £3,000", "From £6,000", "8–10 weeks", "10 years"],
            ["IKEA PAX (self-fit)", "From £400", "From £1,200", "Immediate", "10 years (frame only)"],
          ],
        },
      },
      {
        heading: "How to Save Money on Fitted Wardrobes",
        content: "There are several ways to reduce the cost without compromising quality:",
        list: [
          "Choose matt over gloss finishes — gloss costs 15–20% more",
          "Opt for standard internal layouts rather than fully bespoke configurations",
          "Book during sale periods — Smiths regularly offers 45% off plus additional discounts",
          "Use the price-match promise — Smiths beats any like-for-like competitor quote by 10%",
          "Consider sliding doors for wider openings — they can be more cost-effective than multiple hinged doors",
        ],
      },
    ],
    faqs: [
      { question: "How much do fitted wardrobes cost per metre?", answer: "Fitted wardrobes typically cost £500–£1,500 per linear metre depending on height, depth, door finish, and internal configuration. A standard 2.4m high wardrobe with matt doors and basic internals costs around £500–£700 per metre. Premium finishes with full internal fit-out cost £1,000–£1,500 per metre." },
      { question: "Are fitted wardrobes worth the money?", answer: "Yes — fitted wardrobes typically add 2–5% to property value according to estate agents. They maximise storage by using every inch of available space (including awkward areas), last 20+ years with quality materials, and create a clean, built-in look that freestanding furniture cannot achieve." },
      { question: "Is it cheaper to build your own wardrobe or buy fitted?", answer: "DIY wardrobes using IKEA PAX cost £400–£1,200 but require assembly skills, tools, and time. The result is not truly fitted (gaps at ceiling, walls, and floor). Professional fitted wardrobes cost more upfront but provide a seamless finish, last longer, and add more property value." },
      { question: "How much does a walk-in wardrobe cost?", answer: "A walk-in wardrobe costs £4,999–£12,000 depending on room size, internal configuration, and finishes. A basic walk-in with hanging rails and shelving starts from £4,999. A luxury walk-in with island unit, LED lighting, and full accessories ranges from £8,000–£12,000." },
      { question: "Do Smiths offer payment plans for fitted wardrobes?", answer: "Yes — Smiths offers flexible finance from £500 to £30,000 through Kandoo (FCA-authorised, FRN: 828963). Get an instant decision with no impact on your credit score. Repayment terms from 1 to 7 years." },
    ],
    relatedPages: ["/pricing", "/fitted-wardrobes", "/answers/wardrobe-materials"],
  },
  {
    slug: "how-long-do-fitted-wardrobes-take-to-install",
    title: "How Long Do Fitted Wardrobes Take to Install? | Timeline Guide",
    metaDescription: "Fitted wardrobes take 3 weeks from design visit to installation. Learn the full timeline: measurement, design, manufacturing, and fitting day breakdown.",
    h1: "How Long Do Fitted Wardrobes Take to Install?",
    directAnswer: "From your initial design visit to completed installation, fitted wardrobes from Smiths take approximately 3 weeks. The breakdown is: Day 1 for the design visit and measurement, 2–3 days for 3D design delivery, 2–3 weeks for manufacturing in our London factory, and 1–2 days for on-site installation. This is significantly faster than competitors like Sharps (6–8 weeks) or Hammonds (8–12 weeks).",
    sections: [
      {
        heading: "Full Timeline Breakdown",
        content: "Here is the step-by-step timeline from first contact to finished wardrobes:",
        table: {
          headers: ["Stage", "Duration", "What Happens"],
          rows: [
            ["Design Visit", "Day 1 (50 mins)", "Laser measurement, lifestyle consultation, material selection, instant quote"],
            ["3D Design Delivery", "Within 48 hours", "Photorealistic 3D visualisation sent via email for review"],
            ["Design Approval", "1–3 days", "Revisions if needed, final sign-off, deposit payment"],
            ["Manufacturing", "2–3 weeks", "Built in Smiths' London factory, quality-checked"],
            ["Installation", "1–2 days", "Professional fitting, clean-up, 10-year guarantee begins"],
          ],
        },
      },
      {
        heading: "Factors That Affect Installation Time",
        content: "Several factors can extend or shorten the timeline:",
        list: [
          "Room complexity — loft conversions and angled ceilings add 1–2 days to installation",
          "Number of rooms — multiple rooms are typically installed consecutively",
          "Material availability — standard finishes are always in stock; rare colours may add 1 week",
          "Season — spring and autumn are busiest; winter bookings often have shorter lead times",
          "Design revisions — each revision cycle adds 2–3 days to the overall timeline",
        ],
      },
      {
        heading: "How Smiths Compares to Competitors",
        content: "Smiths' 3-week turnaround is possible because we manufacture in our own London factory rather than outsourcing:",
        table: {
          headers: ["Company", "Total Lead Time", "Manufacturing", "Installation"],
          rows: [
            ["Smiths", "3 weeks", "Own London factory", "1–2 days"],
            ["Sharps", "6–8 weeks", "UK factory", "1–2 days"],
            ["Hammonds", "8–12 weeks", "UK factory", "1–2 days"],
            ["Neville Johnson", "8–10 weeks", "UK factory", "2–3 days"],
            ["Sliderobes", "6–8 weeks", "Ireland factory", "1–2 days"],
          ],
        },
      },
    ],
    faqs: [
      { question: "Can fitted wardrobes be installed in one day?", answer: "Yes — a single wardrobe or small run can typically be installed in one day (6–8 hours). Larger projects (full bedroom suites, walk-in wardrobes, or multiple rooms) take 2 days." },
      { question: "Do I need to empty the room before installation?", answer: "Yes — the room should be cleared of furniture and personal items before our fitters arrive. We provide a checklist 48 hours before installation day." },
      { question: "Will there be much mess during installation?", answer: "Minimal. Our fitters use dust sheets, portable extraction, and clean up completely at the end of each day. Most installations produce only fine sawdust from wall fixings." },
      { question: "Can I use the room during installation?", answer: "We recommend keeping the room clear during the installation day(s). Once complete, you can use the wardrobes immediately — no drying or curing time required." },
    ],
    relatedPages: ["/how-it-works", "/fitted-wardrobes", "/contact"],
  },
  {
    slug: "fitted-wardrobes-vs-freestanding",
    title: "Fitted vs Freestanding Wardrobes: Which Is Better? | Comparison",
    metaDescription: "Fitted wardrobes vs freestanding: compare cost, storage capacity, lifespan, property value, and aesthetics. Find out which is right for your home.",
    h1: "Fitted Wardrobes vs Freestanding: Which Is Better?",
    directAnswer: "Fitted wardrobes are better for maximising storage, adding property value, and creating a seamless built-in look. Freestanding wardrobes are better for renters, tight budgets, and people who move frequently. Fitted wardrobes offer 40–60% more storage capacity than freestanding equivalents in the same space because they use every inch from floor to ceiling with no wasted gaps.",
    sections: [
      {
        heading: "Head-to-Head Comparison",
        content: "A direct comparison across the key factors that matter most:",
        table: {
          headers: ["Factor", "Fitted Wardrobes", "Freestanding Wardrobes"],
          rows: [
            ["Storage Capacity", "40–60% more (floor-to-ceiling, wall-to-wall)", "Limited by standard dimensions"],
            ["Cost", "£1,499–£5,999 (installed)", "£200–£2,000 (self-assembly)"],
            ["Lifespan", "20+ years", "5–15 years"],
            ["Property Value", "Adds 2–5% to home value", "No impact"],
            ["Aesthetics", "Seamless, built-in, no gaps", "Visible gaps, dust traps"],
            ["Customisation", "100% bespoke to room shape", "Standard sizes only"],
            ["Portability", "Fixed (stays with house)", "Can take when moving"],
            ["Installation", "Professional (1–2 days)", "Self-assembly (2–6 hours)"],
            ["Awkward Spaces", "Perfect for slopes, alcoves", "Cannot fit non-standard spaces"],
          ],
        },
      },
      {
        heading: "When to Choose Fitted",
        content: "Fitted wardrobes are the right choice when:",
        list: [
          "You own your home and plan to stay for 5+ years",
          "Your room has awkward features (sloped ceilings, alcoves, chimney breasts)",
          "You need maximum storage from limited floor space",
          "You want a clean, built-in aesthetic with no visible gaps",
          "You want to increase your property's resale value",
        ],
      },
      {
        heading: "When to Choose Freestanding",
        content: "Freestanding wardrobes make more sense when:",
        list: [
          "You rent your home and cannot make permanent changes",
          "You move frequently and want to take furniture with you",
          "Your budget is under £1,000",
          "You need a temporary solution while planning a renovation",
          "The room is a standard rectangular shape with no awkward features",
        ],
      },
    ],
    faqs: [
      { question: "Do fitted wardrobes add value to a house?", answer: "Yes — estate agents report that quality fitted wardrobes add 2–5% to property value. They are particularly valued in London and the South East where bedroom storage is a key selling point. Buyers see fitted wardrobes as a premium feature that saves them the cost and hassle of furnishing." },
      { question: "Can you remove fitted wardrobes?", answer: "Yes, but it requires professional removal and will leave marks on walls, ceiling, and floor that need redecorating. This is why fitted wardrobes are best for homeowners who plan to stay long-term." },
      { question: "Are fitted wardrobes worth it in a small bedroom?", answer: "Absolutely — small bedrooms benefit most from fitted wardrobes because they use every inch of available space. A fitted wardrobe in a small room provides 40–60% more storage than a freestanding equivalent while taking up the same or less floor area." },
      { question: "How long do freestanding wardrobes last?", answer: "Budget freestanding wardrobes (IKEA, Argos) typically last 5–10 years. Mid-range (John Lewis, Habitat) last 10–15 years. High-end solid wood freestanding wardrobes can last 20+ years but cost as much as fitted alternatives." },
    ],
    relatedPages: ["/fitted-wardrobes", "/answers/how-much-do-fitted-wardrobes-cost", "/pricing"],
  },
  {
    slug: "best-wardrobe-door-styles",
    title: "Best Wardrobe Door Styles in 2026 | Hinged, Sliding & Handleless",
    metaDescription: "Compare wardrobe door styles: hinged, sliding, bi-fold, and handleless. Learn which door type suits your room size, budget, and aesthetic preferences.",
    h1: "What Are the Best Wardrobe Door Styles in 2026?",
    directAnswer: "The best wardrobe door styles in 2026 are handleless push-to-open for modern minimalist bedrooms, shaker-style hinged doors for traditional homes, and sliding doors for rooms where space is limited. Handleless designs have grown 300% in popularity since 2023 due to the quiet luxury trend. Sliding doors remain the most practical choice for bedrooms under 12 square metres.",
    sections: [
      {
        heading: "Door Style Comparison",
        content: "Each door type has distinct advantages depending on your room and lifestyle:",
        table: {
          headers: ["Door Style", "Best For", "Space Needed", "Price Range", "Trend Status"],
          rows: [
            ["Handleless Push-to-Open", "Modern, minimalist bedrooms", "Standard clearance", "£££", "Rising (quiet luxury)"],
            ["Shaker Hinged", "Traditional & transitional homes", "60cm door swing", "££", "Evergreen classic"],
            ["Sliding (2-panel)", "Small rooms, tight spaces", "No swing space needed", "££–£££", "Steady demand"],
            ["Sliding (3-panel)", "Wide openings, walk-in entries", "No swing space needed", "£££", "Growing"],
            ["Bi-fold", "Corner wardrobes, deep alcoves", "Half swing of hinged", "£££", "Niche"],
            ["Mirrored Sliding", "Small bedrooms needing light", "No swing space needed", "££", "Steady"],
          ],
        },
      },
      {
        heading: "Trending Finishes for 2026",
        content: "The most popular door finishes this year reflect the shift towards natural, tactile materials:",
        list: [
          "Fluted/ribbed panels — textured vertical grooves that add depth and interest",
          "Matt lacquer in warm neutrals — greige, warm taupe, and soft sage dominate",
          "Woodgrain effect — realistic oak and walnut veneers without the cost of solid wood",
          "Two-tone designs — contrasting frame and panel colours for visual interest",
          "Integrated LED edge lighting — subtle ambient glow around door frames",
        ],
      },
    ],
    faqs: [
      { question: "Are sliding wardrobe doors better than hinged?", answer: "Neither is universally better — it depends on your room. Sliding doors are better for small rooms (no swing space needed) and wide openings. Hinged doors are better for full-width access to contents and traditional aesthetics. Sliding doors cannot open the full width simultaneously." },
      { question: "What is a handleless wardrobe?", answer: "A handleless wardrobe uses a push-to-open mechanism or an integrated J-pull groove instead of visible handles. The result is a completely flat, seamless door surface. This style is associated with the quiet luxury trend and creates a clean, modern aesthetic." },
      { question: "How wide can sliding wardrobe doors be?", answer: "Individual sliding door panels are typically 600mm–1200mm wide. Two-panel systems cover openings of 1200mm–2400mm. Three-panel systems cover 1800mm–3600mm. For wider openings, additional panels or a combination system is used." },
      { question: "Do mirrored wardrobe doors make a room look bigger?", answer: "Yes — mirrored sliding doors reflect light and create the illusion of double the space. They are particularly effective in small bedrooms and rooms with limited natural light. Full-height mirrors also eliminate the need for a separate dressing mirror." },
    ],
    relatedPages: ["/fitted-wardrobes", "/sliding-wardrobes", "/answers/wardrobe-materials"],
  },
  {
    slug: "how-to-organise-a-fitted-wardrobe",
    title: "How to Organise a Fitted Wardrobe | Internal Layout Guide",
    metaDescription: "Learn how to organise a fitted wardrobe with optimal internal layouts for hanging, shelving, drawers, and accessories. Zone-based system for maximum efficiency.",
    h1: "How to Organise a Fitted Wardrobe for Maximum Storage",
    directAnswer: "The most effective way to organise a fitted wardrobe is using a zone-based system: Zone 1 (eye level, 1200–1800mm) for daily items and frequently worn clothes, Zone 2 (above 1800mm) for seasonal storage and luggage, and Zone 3 (below 600mm) for shoes, bags, and heavy items. Allocate 60% to hanging, 20% to shelving, and 20% to drawers for a balanced wardrobe.",
    sections: [
      {
        heading: "The Zone System",
        content: "Professional wardrobe designers use a three-zone system based on how frequently you access items:",
        table: {
          headers: ["Zone", "Height", "Best For", "Access Frequency"],
          rows: [
            ["Zone 1 (Prime)", "1200mm – 1800mm", "Daily clothes, work outfits, accessories", "Multiple times daily"],
            ["Zone 2 (Upper)", "Above 1800mm", "Seasonal items, luggage, spare bedding", "Monthly/seasonal"],
            ["Zone 3 (Lower)", "Below 600mm", "Shoes, bags, heavy items, laundry", "Daily"],
          ],
        },
      },
      {
        heading: "Optimal Internal Layout Ratios",
        content: "For a standard bedroom wardrobe, the ideal internal split is:",
        list: [
          "60% hanging space — split between full-length (dresses, coats) and double-rail (shirts, jackets)",
          "20% shelving — for folded items, knitwear, and accessories",
          "20% drawers — for underwear, socks, jewellery, and delicate items",
          "Add pull-out accessories as needed: trouser racks, tie holders, pull-down rails for high spaces",
        ],
      },
      {
        heading: "Step-by-Step Organisation Method",
        content: "Follow these steps to organise your fitted wardrobe efficiently:",
        list: [
          "Step 1: Empty everything and sort into keep, donate, and discard piles",
          "Step 2: Categorise kept items by type (hanging, folded, accessories, shoes)",
          "Step 3: Measure the volume of each category to determine space allocation",
          "Step 4: Assign zones — daily items at eye level, seasonal items high, heavy items low",
          "Step 5: Use dividers, boxes, and drawer inserts to maintain organisation",
          "Step 6: Review quarterly and rotate seasonal items between zones",
        ],
      },
    ],
    faqs: [
      { question: "How many drawers should a fitted wardrobe have?", answer: "A standard fitted wardrobe should have 4–6 drawers. Allocate 2 drawers for underwear and socks, 1–2 for accessories and jewellery, and 1–2 for folded items like t-shirts or gym wear. Internal drawers (behind doors) are more cost-effective than external chest-of-drawers." },
      { question: "What is the best height for wardrobe hanging rails?", answer: "For double hanging (shirts/jackets above, trousers below): upper rail at 1700mm, lower rail at 900mm. For full-length hanging (dresses, coats): single rail at 1700mm with clear space to floor. For children: lower rail at 900mm–1200mm." },
      { question: "Should I have more hanging or shelving in my wardrobe?", answer: "Most people need more hanging than shelving. The 60/20/20 rule (60% hanging, 20% shelves, 20% drawers) works for most lifestyles. If you wear mostly casual/folded clothes, shift to 40% hanging and 40% shelving." },
      { question: "How do I stop my wardrobe getting messy?", answer: "Use the one-in-one-out rule, fold items using the KonMari method for visibility, add drawer dividers, and do a 5-minute daily reset. The biggest factor is having the right internal configuration — if everything has a designated space, organisation is effortless." },
    ],
    relatedPages: ["/fitted-wardrobes", "/answers/best-wardrobe-door-styles", "/blog/fitted-wardrobe-inside-layouts-storage-solutions"],
  },
  {
    slug: "can-you-fit-wardrobes-in-a-loft-conversion",
    title: "Can You Fit Wardrobes in a Loft Conversion? | Sloped Ceiling Guide",
    metaDescription: "Yes — fitted wardrobes work perfectly in loft conversions. Learn how to maximise storage under sloped ceilings, around dormers, and in knee-wall spaces.",
    h1: "Can You Fit Wardrobes in a Loft Conversion?",
    directAnswer: "Yes — fitted wardrobes are ideal for loft conversions because they can be custom-built to follow the exact angle of your roofline. Unlike freestanding furniture, which leaves large unusable gaps under sloped ceilings, fitted wardrobes use every inch of available space. Knee-wall storage (the low area under the eaves) can be converted into pull-out drawers, shoe racks, or seasonal storage, recovering space that would otherwise be completely wasted.",
    sections: [
      {
        heading: "Types of Loft Wardrobe Solutions",
        content: "Different loft configurations require different approaches:",
        table: {
          headers: ["Loft Type", "Best Solution", "Storage Gained", "Price Range"],
          rows: [
            ["Standard pitched roof", "Angled-top wardrobes following roofline", "70–90% of wall used", "£2,499–£4,999"],
            ["Dormer window loft", "Wardrobes either side of dormer", "Full height sections + angled", "£3,499–£5,999"],
            ["Velux/skylight loft", "Full wall wardrobes avoiding window line", "80–95% of wall used", "£2,999–£5,499"],
            ["Knee-wall (eaves)", "Pull-out drawers and low shelving", "100% of dead space recovered", "£1,499–£2,999"],
            ["L-shaped loft", "Corner wardrobes with angled returns", "Both walls maximised", "£3,999–£6,999"],
          ],
        },
      },
      {
        heading: "How Smiths Handles Sloped Ceilings",
        content: "Our process for loft wardrobes includes:",
        list: [
          "Precision laser measurement of the exact roof angle (to 0.5mm accuracy)",
          "Individual scribing of each panel to follow the roofline with no gaps",
          "Structural assessment to ensure wall fixings are secure in loft timber",
          "Ventilation consideration — ensuring adequate airflow behind wardrobes",
          "LED lighting integration — essential in lofts where natural light is limited",
        ],
      },
    ],
    faqs: [
      { question: "What is the minimum ceiling height for a fitted wardrobe?", answer: "The minimum usable height for a fitted wardrobe section is 900mm — enough for shelving, drawers, or shoe storage. For hanging space, you need at least 1400mm (short hanging) or 1700mm (full-length hanging). Smiths designs loft wardrobes with graduated sections that transition from full-height to low storage as the ceiling drops." },
      { question: "Can you fit sliding doors in a loft?", answer: "Yes, but only on the full-height sections. Where the ceiling slopes below the track height (typically 2000mm minimum for sliding), hinged doors or open shelving are used instead. A common design combines sliding doors on the tall section with push-to-open doors on the angled section." },
      { question: "How do you fix wardrobes to loft walls?", answer: "Loft wardrobes are fixed to the timber studs behind the plasterboard using heavy-duty coach bolts. Our fitters use a stud finder and pilot holes to locate the structural timber. In some cases, additional noggins (horizontal timber braces) are installed between studs for extra fixing points." },
      { question: "Will fitted wardrobes make my loft feel smaller?", answer: "No — fitted wardrobes actually make lofts feel larger by eliminating visual clutter and creating clean, floor-to-ceiling surfaces. Light colours, mirrored panels, and integrated LED lighting further enhance the sense of space." },
    ],
    relatedPages: ["/loft-wardrobes", "/fitted-wardrobes", "/answers/how-much-do-fitted-wardrobes-cost"],
  },
  {
    slug: "what-is-the-best-colour-for-fitted-wardrobes",
    title: "Best Colours for Fitted Wardrobes in 2026 | Colour Guide",
    metaDescription: "The best colours for fitted wardrobes in 2026: warm neutrals, sage green, navy, and natural woodgrains. See trending palettes and room-by-room recommendations.",
    h1: "What Is the Best Colour for Fitted Wardrobes in 2026?",
    directAnswer: "The best colours for fitted wardrobes in 2026 are warm neutrals (greige, warm taupe, cashmere), sage green, and natural woodgrain effects. White remains the safest choice for resale value and small rooms, but the trend has shifted decisively towards warmer, more characterful tones. Dark colours (navy, charcoal, black) work well in large bedrooms with good natural light.",
    sections: [
      {
        heading: "Top 10 Wardrobe Colours for 2026",
        content: "Based on our order data and design consultations, these are the most popular colours this year:",
        table: {
          headers: ["Rank", "Colour", "Finish", "Best Room Type", "Popularity Change"],
          rows: [
            ["1", "Cashmere / Warm Taupe", "Matt", "Any bedroom", "+45% vs 2024"],
            ["2", "Sage Green", "Matt", "Master bedrooms", "+120% vs 2024"],
            ["3", "White (warm tone)", "Matt or Gloss", "Small bedrooms, rentals", "Steady"],
            ["4", "Natural Oak Woodgrain", "Textured", "Scandinavian-style rooms", "+60% vs 2024"],
            ["5", "Charcoal / Anthracite", "Matt", "Large bedrooms, walk-ins", "+30% vs 2024"],
            ["6", "Navy Blue", "Matt", "Statement bedrooms", "Steady"],
            ["7", "Walnut Woodgrain", "Textured", "Traditional/luxury rooms", "+25% vs 2024"],
            ["8", "Dove Grey", "Matt", "Any bedroom", "-10% vs 2024"],
            ["9", "Cream / Ivory", "Matt", "Period properties", "Steady"],
            ["10", "Two-tone (contrast frame)", "Mixed", "Modern bedrooms", "+200% vs 2024"],
          ],
        },
      },
      {
        heading: "Room-by-Room Colour Recommendations",
        content: "Different rooms suit different colour approaches:",
        list: [
          "Small bedrooms — light colours (white, cashmere, light oak) to maximise the sense of space",
          "Large master bedrooms — bolder choices (sage, navy, charcoal) or two-tone designs",
          "Children's rooms — white or light grey (neutral base that grows with the child)",
          "Walk-in wardrobes — darker tones (charcoal, walnut) for a boutique dressing room feel",
          "Loft bedrooms — light colours to compensate for limited natural light",
          "Guest bedrooms — warm neutrals (cashmere, cream) for universal appeal",
        ],
      },
    ],
    faqs: [
      { question: "Should wardrobe doors match the wall colour?", answer: "Not necessarily — contrasting wardrobes can create a stunning design feature. However, matching or toning with walls creates a seamless, room-enlarging effect. The current trend is for wardrobes to be 2–3 shades darker than walls for subtle contrast." },
      { question: "Are white wardrobes outdated?", answer: "No — white wardrobes remain the most versatile and resale-friendly choice. However, the trend has shifted from cool/bright white to warm white tones. Pure brilliant white now looks dated; warm white, cashmere, and ivory are the modern equivalents." },
      { question: "Do dark wardrobes make a room look smaller?", answer: "Dark wardrobes can make a small room feel smaller, but in rooms over 14 square metres with good natural light, they create a sophisticated, cocooning atmosphere. Mirrored panels or glass inserts can offset the visual weight of dark doors." },
      { question: "Can I mix colours on my fitted wardrobes?", answer: "Yes — two-tone wardrobes are one of the biggest trends in 2026 (+200% growth). Common combinations include: light frame with dark doors, woodgrain surround with matt centre panels, or alternating door colours for a statement wall." },
    ],
    relatedPages: ["/fitted-wardrobes", "/answers/best-wardrobe-door-styles", "/blog/fitted-wardrobe-colours-2026"],
  },
  {
    slug: "do-fitted-wardrobes-add-value-to-your-home",
    title: "Do Fitted Wardrobes Add Value to Your Home? | Property Guide",
    metaDescription: "Fitted wardrobes add 2–5% to property value. Learn how much value they add, which types increase value most, and what estate agents say about fitted furniture.",
    h1: "Do Fitted Wardrobes Add Value to Your Home?",
    directAnswer: "Yes — quality fitted wardrobes add 2–5% to property value according to UK estate agents. For a £500,000 property, that represents £10,000–£25,000 in added value. The return on investment is highest for master bedrooms, walk-in wardrobes, and properties in London and the South East where storage is a premium selling point.",
    sections: [
      {
        heading: "Value Added by Wardrobe Type",
        content: "Different types of fitted furniture add different levels of value:",
        table: {
          headers: ["Furniture Type", "Typical Cost", "Value Added", "ROI"],
          rows: [
            ["Master bedroom wardrobes", "£3,000–£5,000", "£8,000–£15,000", "200–300%"],
            ["Walk-in wardrobe", "£5,000–£12,000", "£12,000–£25,000", "150–250%"],
            ["Full bedroom suite", "£5,000–£8,000", "£10,000–£20,000", "150–250%"],
            ["Fitted kitchen", "£8,000–£20,000", "£15,000–£40,000", "150–200%"],
            ["Under-stairs storage", "£1,500–£3,000", "£3,000–£5,000", "150–200%"],
            ["Loft wardrobes", "£2,500–£5,000", "£5,000–£10,000", "150–200%"],
          ],
        },
      },
      {
        heading: "What Estate Agents Say",
        content: "Key findings from estate agent surveys on fitted furniture and property value:",
        list: [
          "89% of estate agents say fitted wardrobes make a property easier to sell",
          "74% say buyers specifically ask about bedroom storage during viewings",
          "Fitted wardrobes are the #3 most-requested feature after kitchens and bathrooms",
          "Properties with fitted wardrobes sell 2–3 weeks faster on average",
          "Walk-in wardrobes are considered a luxury feature that commands premium pricing",
        ],
      },
    ],
    faqs: [
      { question: "Do fitted wardrobes increase property value more than freestanding?", answer: "Yes — fitted wardrobes are considered a permanent home improvement that adds to the property's value. Freestanding furniture is personal property that leaves with the seller and adds no value to the house itself." },
      { question: "Should I install fitted wardrobes before selling my house?", answer: "If your bedrooms lack adequate storage, yes — it's one of the highest-ROI improvements you can make. Focus on the master bedroom first. Neutral colours (white, cashmere, light grey) appeal to the widest range of buyers." },
      { question: "Do buyers prefer fitted or freestanding wardrobes?", answer: "74% of buyers prefer fitted wardrobes according to estate agent surveys. They value the storage efficiency, clean aesthetic, and the fact that they don't need to buy and install their own furniture." },
      { question: "Which rooms should I prioritise for fitted furniture?", answer: "Priority order for maximum property value: 1) Master bedroom wardrobes, 2) Kitchen, 3) Second bedroom wardrobes, 4) Under-stairs storage, 5) Home office. The master bedroom has the highest ROI because it's the room buyers scrutinise most." },
    ],
    relatedPages: ["/fitted-wardrobes", "/answers/how-much-do-fitted-wardrobes-cost", "/blog/do-fitted-wardrobes-add-value"],
  },
  {
    slug: "what-to-expect-during-a-wardrobe-design-visit",
    title: "What to Expect During a Free Wardrobe Design Visit | Guide",
    metaDescription: "A free wardrobe design visit takes 50 minutes. Learn what happens: laser measurement, lifestyle consultation, material selection, 3D design, and instant quote.",
    h1: "What to Expect During a Free Wardrobe Design Visit",
    directAnswer: "A free wardrobe design visit from Smiths takes approximately 50 minutes and includes four stages: precision laser measurement of your room (15 mins), a lifestyle and storage consultation (15 mins), material and finish selection with physical samples (10 mins), and an instant fixed-price quote (10 mins). Within 48 hours you receive a photorealistic 3D visualisation of your new wardrobe. There is no obligation and no pressure to commit.",
    sections: [
      {
        heading: "The Four Stages",
        content: "Here is exactly what happens during your design visit:",
        table: {
          headers: ["Stage", "Duration", "What Happens", "What You Need to Do"],
          rows: [
            ["1. Laser Measurement", "15 mins", "Designer measures every dimension including ceiling height, alcoves, angles", "Just show them the room"],
            ["2. Lifestyle Consultation", "15 mins", "Discussion about storage needs, daily routines, preferences", "Share how you use the space"],
            ["3. Material Selection", "10 mins", "Browse 50+ door finishes with physical samples", "Choose colours and styles you like"],
            ["4. Instant Quote", "10 mins", "Detailed fixed-price quote based on your exact requirements", "Ask any questions"],
          ],
        },
      },
      {
        heading: "How to Prepare",
        content: "To get the most from your design visit:",
        list: [
          "Clear access to the walls where wardrobes will go (move furniture if possible)",
          "Think about what you need to store — clothes, shoes, bags, accessories, bedding",
          "Browse our gallery or Pinterest for style inspiration",
          "Note any room challenges — radiators, light switches, sockets, sloped ceilings",
          "Have any competitor quotes ready if you want us to price-match",
        ],
      },
    ],
    faqs: [
      { question: "Is the design visit really free?", answer: "Yes — completely free with no obligation. You receive a detailed quote and 3D design at no cost. There is no deposit required until you decide to proceed." },
      { question: "Do I need to be home for the design visit?", answer: "Yes — the designer needs access to measure the room and discuss your requirements. Both partners should ideally be present if it's a joint decision." },
      { question: "How soon can I book a design visit?", answer: "Design visits are typically available within 3–5 working days. Evening and weekend appointments are available." },
      { question: "What happens after the design visit?", answer: "Within 48 hours you receive a photorealistic 3D visualisation by email. You can request revisions at no cost. Once you approve the design and pay a deposit, manufacturing begins immediately." },
    ],
    relatedPages: ["/contact", "/how-it-works", "/fitted-wardrobes/what-to-expect-design-visit"],
  },
  {
    slug: "sliding-vs-hinged-wardrobe-doors",
    title: "Sliding vs Hinged Wardrobe Doors: Which Is Better? | Comparison",
    metaDescription: "Compare sliding vs hinged wardrobe doors: space requirements, cost, access, durability, and aesthetics. Find the right door type for your bedroom.",
    h1: "Sliding vs Hinged Wardrobe Doors: Which Should You Choose?",
    directAnswer: "Choose sliding doors if your bedroom is under 12 square metres or if the wardrobe faces the bed with limited floor space. Choose hinged doors if you want full simultaneous access to all contents and prefer a traditional aesthetic. Sliding doors save 60cm of floor space (no swing clearance needed) but only reveal half the wardrobe at a time. Hinged doors cost 10–15% less but require clear floor space in front.",
    sections: [
      {
        heading: "Direct Comparison",
        content: "A side-by-side comparison of the two main door types:",
        table: {
          headers: ["Factor", "Sliding Doors", "Hinged Doors"],
          rows: [
            ["Space Required", "No swing clearance (0cm)", "60cm clearance per door"],
            ["Access", "50% visible at once", "100% visible when open"],
            ["Cost", "10–15% more", "Standard pricing"],
            ["Durability", "Track mechanism (20+ years)", "Hinge mechanism (20+ years)"],
            ["Max Width", "Up to 4000mm (4 panels)", "Up to 600mm per door"],
            ["Noise", "Silent soft-close tracks", "Soft-close hinges (quiet)"],
            ["Aesthetics", "Modern, minimal", "Classic, versatile"],
            ["Mirror Option", "Full-height mirror panels", "Mirror inserts possible"],
            ["Maintenance", "Track cleaning annually", "Hinge adjustment every 2–3 years"],
          ],
        },
      },
      {
        heading: "When Sliding Doors Are Better",
        content: "Sliding doors are the optimal choice in these situations:",
        list: [
          "Small bedrooms where door swing would block walkways or hit furniture",
          "Wardrobes facing the bed with limited space between",
          "Wide openings (over 2000mm) where multiple hinged doors look busy",
          "When you want full-height mirrors without a separate mirror",
          "Modern/minimalist design schemes",
        ],
      },
      {
        heading: "When Hinged Doors Are Better",
        content: "Hinged doors are preferable when:",
        list: [
          "You want to see all contents at once (both sides open simultaneously)",
          "The wardrobe is in an alcove with adequate clearance",
          "Budget is a primary concern (10–15% cheaper)",
          "You prefer a traditional or shaker-style aesthetic",
          "The wardrobe is narrow (under 1500mm) — sliding panels would be too small",
        ],
      },
    ],
    faqs: [
      { question: "Are sliding wardrobe doors more expensive than hinged?", answer: "Yes — sliding doors typically cost 10–15% more than hinged doors for the same wardrobe size. The additional cost comes from the track mechanism, heavier door panels, and more complex installation. However, sliding doors eliminate the need for door handles, which partially offsets the cost." },
      { question: "Do sliding wardrobe doors break easily?", answer: "No — modern sliding door mechanisms (like those used by Smiths) use heavy-duty aluminium tracks with soft-close dampeners and are rated for 100,000+ cycles. The most common issue is track debris, which is solved by annual cleaning. Smiths' 10-year guarantee covers the mechanism." },
      { question: "Can you convert hinged wardrobes to sliding?", answer: "In most cases, yes — the carcass (internal box) remains the same, and new sliding door panels and tracks are fitted to the front. This is a cost-effective way to modernise existing fitted wardrobes without replacing the entire unit." },
      { question: "What is the minimum width for sliding wardrobe doors?", answer: "The minimum practical width for a 2-panel sliding system is 1200mm (two 600mm panels). Below this width, the panels become too narrow to be practical and hinged doors are recommended instead." },
    ],
    relatedPages: ["/sliding-wardrobes", "/fitted-wardrobes", "/answers/best-wardrobe-door-styles"],
  },
  {
    slug: "how-to-measure-for-fitted-wardrobes",
    title: "How to Measure for Fitted Wardrobes | DIY Measurement Guide",
    metaDescription: "Learn how to measure your room for fitted wardrobes: width, height, depth, and common obstacles. Get accurate measurements before your design visit.",
    h1: "How to Measure for Fitted Wardrobes",
    directAnswer: "To measure for fitted wardrobes, you need three key measurements: width (wall-to-wall at floor, middle, and ceiling level), height (floor-to-ceiling at left, centre, and right), and available depth (minimum 550mm for standard hanging, 600mm for coat hanging). Always measure in millimetres and take each measurement at three points because walls and floors are rarely perfectly level. Note any obstacles: sockets, light switches, radiators, coving, and skirting boards.",
    sections: [
      {
        heading: "Step-by-Step Measurement Guide",
        content: "Follow these steps for accurate measurements:",
        list: [
          "Step 1: Measure WIDTH at three heights — floor level, 1200mm up, and ceiling level. Record the smallest measurement.",
          "Step 2: Measure HEIGHT at three points — left side, centre, and right side. Record the smallest measurement.",
          "Step 3: Measure DEPTH available — from the back wall to where the wardrobe front will sit. Minimum 550mm for hanging.",
          "Step 4: Note OBSTACLES — mark the position of sockets, switches, radiators, pipes, and coving on a sketch.",
          "Step 5: Check FLOOR LEVEL — place a spirit level across the floor. Note any slope (our fitters compensate with adjustable legs).",
          "Step 6: Check WALL STRAIGHTNESS — hold a long straight edge against each wall. Note any bowing.",
        ],
      },
      {
        heading: "Common Measurements You'll Need",
        content: "Reference dimensions for planning your wardrobe:",
        table: {
          headers: ["Measurement", "Standard", "Minimum", "Notes"],
          rows: [
            ["Hanging depth", "600mm", "550mm", "550mm for shirts, 600mm for coats"],
            ["Full-length hanging height", "1700mm", "1400mm", "Dresses, coats, long items"],
            ["Double-rail hanging (each)", "900mm", "800mm", "Shirts above, trousers below"],
            ["Shelf depth", "400mm–500mm", "300mm", "Deeper for folded jumpers"],
            ["Drawer depth", "450mm", "350mm", "Internal drawers behind doors"],
            ["Shoe shelf height", "150mm–200mm", "150mm", "Per pair, angled shelves save space"],
          ],
        },
      },
      {
        heading: "Why Professional Measurement Is Better",
        content: "While DIY measurements give you a rough idea, professional laser measurement is essential for the final design because:",
        list: [
          "Laser measures are accurate to 0.5mm vs 2–3mm for tape measures",
          "Professionals check for hidden obstacles (pipes, cables behind walls)",
          "They assess wall construction (plasterboard vs brick) for fixing methods",
          "They identify structural issues that could affect installation",
          "The measurement is free — included in your no-obligation design visit",
        ],
      },
    ],
    faqs: [
      { question: "Do I need to measure before booking a design visit?", answer: "No — our designer brings professional laser measuring equipment and takes all measurements during the free design visit. However, having rough measurements helps you discuss options and get a ballpark price estimate beforehand." },
      { question: "How accurate do my measurements need to be?", answer: "For planning purposes, measurements within 10mm are fine. For manufacturing, we need 0.5mm accuracy — which is why we use laser measurement during the design visit. Never order fitted furniture based on DIY measurements alone." },
      { question: "What if my walls are not straight?", answer: "Most walls have some irregularity. Our wardrobes are designed with scribe panels (thin filler strips) that are trimmed on-site to follow the exact wall contour. This creates a seamless fit even against uneven walls." },
      { question: "What is the minimum room size for fitted wardrobes?", answer: "There is no minimum room size — fitted wardrobes can be designed for any space. Even a 500mm-wide alcove can accommodate useful shelving or a narrow wardrobe. The minimum depth for hanging clothes is 550mm." },
    ],
    relatedPages: ["/contact", "/how-it-works", "/fitted-wardrobes"],
  },
];
